'use client';

import { User } from '@api/models';
import { CalendarX2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useParams } from 'next/navigation';

import { Button } from '@ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/components/ui/tooltip';
import { useIsMobile } from '@website/hooks';
import { useActivityAvailabilitySummary } from '@website/hooks/useActivityAvailabilitySummary';
import { useAuthenticatedNavigation } from '@website/hooks/useAuthenticatedNavigation';
import { useModeSwitcher } from '@website/hooks/useModeSwitcher';

import type { Activity } from '../../../../../services/activities.service';

interface ViewSlotsButtonProps {
  activity: Activity;
  user: User | null;
}

/**
 * Lightweight "view available slots" CTA shown to every viewer who is NOT the
 * activity's owning professional — anonymous visitors, learners, and mentors
 * who don't own this activity.
 *
 * Kept out of ActivityActionButtons (the owner toolbar, which transitively
 * pulls Stripe Connect + react-day-picker + Luxon + the create-slot dialog) so
 * the public booking CTA can render immediately without that payload. Anonymous
 * clicks navigate to the slot picker, which bounces to login server-side.
 */
export function ViewSlotsButton({ activity, user }: ViewSlotsButtonProps) {
  const t = useTranslations('ActivityDetail');
  const params = useParams();
  const locale = params.locale as string;
  const isMobile = useIsMobile();

  const { navigateWithAuth } = useAuthenticatedNavigation({ user });

  // Non-owner mentors must flip to learner before booking. Open the shared
  // mode-switch confirm dialog (returnTo wired back into the slot picker)
  // rather than silently mutating their role.
  const slotsReturnTo = `/${locale}/activity/${activity.id}/slots`;
  const slotsModeSwitcher = useModeSwitcher(user ?? undefined, { returnTo: slotsReturnTo });

  // Same live summary the header island reads (shared query cache). It only
  // counts future, open, visible occurrences, so no slot type = nothing to book.
  const { data: summary } = useActivityAvailabilitySummary(activity.id);
  const isNotBookableYet = !!summary && !summary.hasSingle && !summary.hasGroup;

  const viewerIsMentor = !!user?.isProfessional;
  const handleViewSlotsClick = () => {
    if (viewerIsMentor) {
      slotsModeSwitcher.open();
      return;
    }
    navigateWithAuth(`/activity/${activity.id}/slots`);
  };

  const notBookableYet = (
    <div className="flex flex-col gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
      <div className="flex items-start gap-2">
        <CalendarX2 className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" aria-hidden="true" />
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-neutral-800">{t('noAvailabilityYetTitle')}</span>
          <span className="text-sm text-neutral-600">{t('noAvailabilityYetText')}</span>
        </div>
      </div>
      {activity.professionalId && (
        <Button variant="outline" className="w-full" asChild>
          <Link href={`/${locale}/professional/${activity.professionalId}`}>{t('viewProfessionalProfile')}</Link>
        </Button>
      )}
    </div>
  );

  const button = isNotBookableYet ? (
    notBookableYet
  ) : (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="default"
            className="w-full py-3"
            onClick={handleViewSlotsClick}
            disabled={slotsModeSwitcher.isPending}
          >
            {t('viewAvailableSlots')}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('viewAvailableSlotsTooltip')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  if (isMobile) {
    return (
      <>
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200 px-4 py-3 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]">
          {button}
        </div>
        {slotsModeSwitcher.dialogs}
      </>
    );
  }

  return (
    <>
      {button}
      {slotsModeSwitcher.dialogs}
    </>
  );
}
